import { Parser } from 'html-to-react';
import RowDescriptionCell from '@app/pages/stepper/showcase/fragments/row-description-cell/index';
import Photo from '@app/pages/stepper/showcase/fragments/photo/index';
import { TableRowsNames } from '@app/pages/stepper/showcase/constants/table-rows';
import SimpleText from '@app/pages/stepper/showcase/fragments/simple-text/index';
import MotivationToChange from '@app/pages/stepper/showcase/fragments/motivation/index';
import Summary from '@app/pages/stepper/showcase/fragments/summary/index';
import Location from '@app/pages/stepper/showcase/fragments/location/index';
import Skills from '@app/pages/stepper/showcase/fragments/skills/index';
import { SkillTypes } from '@utils/hooks/application/use-skills';
import { ShowcaseOtherSelections } from '@app/pages/stepper/showcase/ShowcaseOtherSelections';
import { formatLinebreak } from '@utils/functions';
import { GlobalCompensation } from '@components/Organisms/GlobalCompensation/index';
import { getCompensationPackageValues } from '@components/Organisms/GlobalCompensation/_helpers/bonuses-and-benefits';
import i18n from '@app/i18n';
import { useStoreState } from '@app/store/hooks';
import { jobStoreSelector } from '@app/model/Job/Job.selectors';
import { Trans } from 'react-i18next';

/**
 * Compose the Showcase table's rows.
 *
 * @param arePayTransparencyRulesApplied
 * @param candidates
 * @param tableRow
 * @returns Object
 */
const tableDataFactory = (
  arePayTransparencyRulesApplied,
  candidates,
  tableRow,
) => {
  const htmlParser = new Parser();

  return candidates.reduce(
    (result, candidate) => {
      const { desiredAnnualSalary, currentAnnualSalary } =
        candidate.compensationPackage ?? {};

      switch (tableRow.id) {
        case TableRowsNames.PHOTO:
          return { ...result, [candidate.id]: <Photo {...candidate} /> };

        case TableRowsNames.AGE: {
          const currentYear = new Date().getFullYear();
          const age = candidate.yearOfBirth
            ? currentYear - candidate.yearOfBirth
            : null;

          return {
            ...result,
            [candidate.id]: <SimpleText text={age} />,
          };
        }

        case TableRowsNames.SENIORITY:
          return {
            ...result,
            [candidate.id]: <SimpleText text={candidate.seniority} />,
          };

        case TableRowsNames.COMPANY:
          const companyContent = candidate.isFreelancer
            ? i18n.t('showcase.company.freelance')
            : candidate.currentCompanyName;

          return {
            ...result,
            [candidate.id]: <SimpleText text={companyContent} />,
          };

        case TableRowsNames.OTHER_SELECTIONS:
          return {
            ...result,
            [candidate.id]: (
              <ShowcaseOtherSelections
                isCandidateActivelySearching={candidate.isActivelySearching}
                candidateCurrentSelectionProcessesCount={
                  candidate.currentSelectionProcessesCount
                }
              />
            ),
          };

        case TableRowsNames.MOTIVATION:
          return {
            ...result,
            [candidate.id]: (
              <MotivationToChange
                candidateMotivationToChange={candidate.motivationToChange}
              />
            ),
          };

        case TableRowsNames.SUMMARY:
          return {
            ...result,
            [candidate.id]: (
              <Summary
                summary={htmlParser.parse(formatLinebreak(candidate.summary))}
                isReadMoreEnabled={
                  candidate.summary && candidate.summary.length > 200
                }
              />
            ),
          };

        case TableRowsNames.LOCATION:
          return { ...result, [candidate.id]: <Location {...candidate} /> };

        case TableRowsNames.MUST_HAVE:
          return {
            ...result,
            [candidate.id]: (
              <Skills {...candidate} filterType={SkillTypes.MUST_HAVE} />
            ),
          };

        case TableRowsNames.NICE_TO_HAVE:
          return {
            ...result,
            [candidate.id]: (
              <Skills {...candidate} filterType={SkillTypes.NICE_TO_HAVE} />
            ),
          };

        case TableRowsNames.NON_COMPETE_AGREEMENT:
          return {
            ...result,
            [candidate.id]: (
              <SimpleText text={candidate.nonCompeteAgreement || '-'} />
            ),
          };

        case TableRowsNames.NOTICE_PERIOD:
          return {
            ...result,
            [candidate.id]: <SimpleText text={candidate.noticePeriod || '-'} />,
          };

        case TableRowsNames.CURRENT_GLOBAL_COMPENSATION:
          const totalCurrentCompensationPackage = getCompensationPackageValues({
            candidate,
            isDesired: false,
          }).total;

          const isCompensationPackageVisible =
            arePayTransparencyRulesApplied && !totalCurrentCompensationPackage;

          const cellContent = isCompensationPackageVisible ? (
            <p className="c-app-showcase__pay-transparency-disclaimer">
              <Trans i18nKey="showcase.currentGlobalCompensation.payTransparencyDisclaimer" />
            </p>
          ) : (
            <GlobalCompensation candidate={candidate} />
          );

          return {
            ...result,
            [candidate.id]: (
              <td className="c-app-showcase__cell">{cellContent}</td>
            ),
          };

        case TableRowsNames.DESIRED_GLOBAL_COMPENSATION:
          return {
            ...result,
            [candidate.id]: (
              <td className="c-app-showcase__cell">
                <GlobalCompensation candidate={candidate} isDesired />
              </td>
            ),
          };

        default:
          return {
            ...result,
            [candidate.id]: <td className="c-app-showcase__cell"></td>,
          };
      }
    },
    {
      'initial-column': (
        <RowDescriptionCell
          name={tableRow.id}
          hasVisibilityAction={tableRow.hasVisibilityAction}
        />
      ),
    },
  );
};

export default tableDataFactory;
